import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  LinearProgress,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Pagination, // <-- ДОБАВЛЕНО
} from '@mui/material';
import {
  People,
  Tour,
  CalendarMonth,
  UploadFile,
  ReceiptLong,
  TrendingUp,
  Warning,
  Add,
  Star,
  EmojiEvents,
  EventNote,
  Extension,
  Dashboard as DashboardIcon,
  Groups,
  Delete,
  Check,
  Info,
  AccessTime,
  Person,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../utils/api';
import DashboardBanner from './DashboardBanner';
import { extractArrayFromResponse } from '../utils/apiHelpers';

interface Stats {
  guidesCount: number;
  toursCount: number;
  recordsCount: number;
  uploadsCount: number;
  totalAmount: number;
}

interface TopGuide {
  name: string;
  tours_count: number;
  total_people: number;
}

interface TopPopularizer {
  name: string;
  short_name: string;
  tours_count: number;
  total_people: number;
}

interface TopTour {
  name: string;
  count: number;
  price: number;
}

interface Period {
  id: number;
  month_name: string;
  year: number;
  is_active: number;
  start_day: number;
  end_day: number;
}

interface ExtraProgram {
  id: number;
  name: string;
  rate: string;
  is_active: number;
}

interface SelectedProgram {
  extraProgramId: number;
  name: string;
  hours: number;
  rate: number;
  id?: number;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  cursor: 'pointer',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [topGuides, setTopGuides] = useState<TopGuide[]>([]);
  const [topPopularizers, setTopPopularizers] = useState<TopPopularizer[]>([]);
  const [topTours, setTopTours] = useState<TopTour[]>([]);
  const [guidesWithoutCalendar, setGuidesWithoutCalendar] = useState<any[]>([]);
  const [guidesWithoutCalendarCount, setGuidesWithoutCalendarCount] = useState(0);
  const [showGuidesWithoutCalendar, setShowGuidesWithoutCalendar] = useState(false);
  const [activePeriod, setActivePeriod] = useState<Period | null>(null);

  const [todayExcs, setTodayExcs] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loadingExcs, setLoadingExcs] = useState(false);
  
  // СОСТОЯНИЯ ДЛЯ ПАГИНАЦИИ
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>(6);

  useEffect(() => {
    setPage(1); // Сброс страницы при смене даты
    const loadDayOrders = async () => {
      try {
        console.log('SENDING DATE:', selectedDate);
        const res = await axios.get(`/api/calendar/day/${selectedDate}`);
        console.log('GOT DATA:', res.data?.length, 'items');
        setTodayExcs(res.data || []);
      } catch (e) {
        console.error('Ошибка загрузки заявок на дату:', e);
        setTodayExcs([]);
      }
    };
    loadDayOrders();
  }, [selectedDate]);

  const [openExtraDialog, setOpenExtraDialog] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState<any>(null);
  const [extraPrograms, setExtraPrograms] = useState<ExtraProgram[]>([]);
  const [selectedExtraProgram, setSelectedExtraProgram] = useState<number>(0);
  const [hours, setHours] = useState<number>(1);
  const [rate, setRate] = useState<number>(0);
  const [selectedPrograms, setSelectedPrograms] = useState<SelectedProgram[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [duplicateWarning, setDuplicateWarning] = useState<{ show: boolean; programName: string; existingHours: number }>({
    show: false,
    programName: '',
    existingHours: 0,
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const periodsRes = await api.get('/periods');
      const periods = Array.isArray(periodsRes) ? periodsRes : periodsRes?.data || [];
      const active = periods.find((p: Period) => p.is_active === 1);
      setActivePeriod(active || null);

      // Автовыбор даты по активному периоду:
      // если сегодня вне периода — открываем первое число активного периода
      if (active) {
        const yyyy = active.year;
        const mm = String(active.month_number).padStart(2, '0');
        const startDate = `${yyyy}-${mm}-${String(active.start_day).padStart(2, '0')}`;
        const endDate = `${yyyy}-${mm}-${String(active.end_day).padStart(2, '0')}`;
        const today = new Date().toISOString().split('T')[0];
        if (today < startDate || today > endDate) {
          setSelectedDate(startDate);
        }
      }

      const statsRes = await api.get('/stats/full');
      const statsData = statsRes?.data || statsRes || {};
      const periodStats = statsData.stats || {};
      const topGuidesData = statsData.topGuides || [];
      const topToursData = statsData.topTours || [];

      setStats({
        guidesCount: periodStats.guides_count || 0,
        toursCount: topToursData.length || 0,
        recordsCount: periodStats.records_count || 0,
        uploadsCount: statsData.uploads_count || 0,
        totalAmount: statsData.totalAmount || 0,
      });

      setTopGuides(topGuidesData);

      const [topPopularizersRes, guidesWithoutCalendarRes] = await Promise.all([
        api.get('/stats/top-popularizers'),
        api.get('/stats/guides-without-calendar'),
      ]);

      setTopPopularizers(topPopularizersRes?.data || []);
      setTopTours(topToursData);

      const guidesWithoutData = guidesWithoutCalendarRes?.data || { guides: [], count: 0 };
      setGuidesWithoutCalendar(guidesWithoutData.guides || []);
      setGuidesWithoutCalendarCount(guidesWithoutData.count || 0);
    } catch (error) {
      console.error('Ошибка загрузки дашборда:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // fetchExcursions(selectedDate); // Удалено: дублирующий запрос
  }, [selectedDate]);

  const handleCardClick = (path: string) => {
    navigate(path);
  };

  const handleOpenExtraDialog = async (guide: any) => {
    setSelectedGuide(guide);
    setOpenExtraDialog(true);
    setSelectedExtraProgram(0);
    setHours(1);
    setRate(0);
    setDuplicateWarning({ show: false, programName: '', existingHours: 0 });
    setLoadingExisting(true);

    try {
      const data = await api.get('/extra-payments/active');
      const programs = extractArrayFromResponse<ExtraProgram>(data);
      setExtraPrograms(programs);

      if (activePeriod) {
        const guideData = await api.get(`/calculate/guide/${guide.id}/period/${activePeriod.id}`);
        const existingPrograms = guideData?.data?.extraPrograms || [];
        const loadedPrograms: SelectedProgram[] = existingPrograms.map((ep: any) => ({
          id: ep.id,
          extraProgramId: ep.extra_program_id,
          name: ep.name,
          hours: ep.hours,
          rate: ep.rate,
        }));
        setSelectedPrograms(loadedPrograms);
        if (loadedPrograms.length > 0) {
          setSnackbar({
            open: true,
            message: `Загружено ${loadedPrograms.length} существующих программ`,
            severity: 'success',
          });
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка загрузки существующих программ',
        severity: 'error',
      });
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleCloseExtraDialog = () => {
    setOpenExtraDialog(false);
    setSelectedGuide(null);
    setSelectedPrograms([]);
    setSelectedExtraProgram(0);
    setHours(1);
    setRate(0);
    setDuplicateWarning({ show: false, programName: '', existingHours: 0 });
  };

  const handleProgramSelect = (programId: number) => {
    setSelectedExtraProgram(programId);
    const program = extraPrograms.find(p => p.id === programId);
    if (program) {
      setRate(Number(program.rate) || 0);
    }
    const existing = selectedPrograms.find(p => p.extraProgramId === programId);
    if (existing) {
      setDuplicateWarning({
        show: true,
        programName: existing.name,
        existingHours: existing.hours,
      });
    } else {
      setDuplicateWarning({ show: false, programName: '', existingHours: 0 });
    }
  };

  const handleAddProgram = () => {
    if (!selectedExtraProgram || hours <= 0 || rate <= 0) {
      setSnackbar({
        open: true,
        message: 'Заполните все поля',
        severity: 'error',
      });
      return;
    }

    const program = extraPrograms.find(p => p.id === selectedExtraProgram);
    if (!program) return;

    const existingIndex = selectedPrograms.findIndex(p => p.extraProgramId === selectedExtraProgram);

    if (existingIndex !== -1) {
      const updated = [...selectedPrograms];
      updated[existingIndex] = {
        ...updated[existingIndex],
        hours: updated[existingIndex].hours + hours,
        rate: rate,
      };
      setSelectedPrograms(updated);

      setSnackbar({
        open: true,
        message: `Программа "${program.name}" обновлена (часов: ${updated[existingIndex].hours})`,
        severity: 'success',
      });

      setDuplicateWarning({ show: false, programName: '', existingHours: 0 });
    } else {
      setSelectedPrograms([
        ...selectedPrograms,
        {
          extraProgramId: selectedExtraProgram,
          name: program.name,
          hours: hours,
          rate: rate,
        },
      ]);

      setSnackbar({
        open: true,
        message: `Программа "${program.name}" добавлена в список`,
        severity: 'success',
      });
    }

    setSelectedExtraProgram(0);
    setHours(1);
    setRate(0);
  };

  const handleRemoveProgram = (programId: number) => {
    const program = selectedPrograms.find(p => p.extraProgramId === programId);
    setSelectedPrograms(selectedPrograms.filter(p => p.extraProgramId !== programId));
    if (program) {
      setSnackbar({
        open: true,
        message: `Программа "${program.name}" удалена`,
        severity: 'success',
      });
    }
  };

  const handleApplyPrograms = async () => {
    if (selectedPrograms.length === 0) {
      setSnackbar({
        open: true,
        message: 'Добавьте хотя бы одну программу',
        severity: 'error',
      });
      return;
    }

    if (!selectedGuide || !activePeriod) {
      setSnackbar({
        open: true,
        message: 'Ошибка: не выбран гид или период',
        severity: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      await api.post('/extra-payments/assign-multiple', {
        guideId: selectedGuide.id,
        periodId: activePeriod.id,
        programs: selectedPrograms.map(p => ({
          extraProgramId: p.extraProgramId,
          hours: p.hours,
          rate: p.rate,
        })),
      });

      setSnackbar({
        open: true,
        message: `Назначено ${selectedPrograms.length} программ для ${selectedGuide.name}`,
        severity: 'success',
      });

      handleCloseExtraDialog();
      await loadData();
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при назначении дополнительных программ',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  const periodText = activePeriod
    ? `${activePeriod.month_name} ${activePeriod.year} (с ${String(activePeriod.start_day).padStart(2, '0')} по ${String(activePeriod.end_day).padStart(2, '0')})`
    : 'Не выбран';

  const statCards = [
    {
      title: 'Экскурсоводы',
      value: stats?.guidesCount || 0,
      icon: <People sx={{ fontSize: 32 }} />,
      color: '#1976d2',
      path: '/guides',
    },
    {
      title: 'Программы',
      value: stats?.toursCount || 0,
      icon: <Tour sx={{ fontSize: 32 }} />,
      color: '#2e7d32',
      path: '/tours',
    },
    {
      title: 'Записей в календаре',
      value: stats?.recordsCount || 0,
      icon: <CalendarMonth sx={{ fontSize: 32 }} />,
      color: '#ed6c02',
      path: '/calendar',
    },
    {
      title: 'Загрузок',
      value: stats?.uploadsCount || 0,
      icon: <UploadFile sx={{ fontSize: 32 }} />,
      color: '#9c27b0',
      path: '/upload',
    },
    {
      title: 'Общая сумма',
      value: stats?.totalAmount ? `${stats.totalAmount.toLocaleString('ru-RU')} ₽` : '0 ₽',
      icon: <ReceiptLong sx={{ fontSize: 32 }} />,
      color: '#d32f2f',
      path: '/summary',
    },
    {
      title: 'Без календаря',
      value: guidesWithoutCalendarCount,
      icon: <Warning sx={{ fontSize: 32 }} />,
      color: '#e65100',
      path: '#',
      onClick: () => setShowGuidesWithoutCalendar(!showGuidesWithoutCalendar),
    },
  ];

  // ЛОГИКА ПАГИНАЦИИ (перед return)
  const limit = itemsPerPage === 'all' ? todayExcs.length : itemsPerPage;
  const displayedExcs = todayExcs.slice((page - 1) * limit, page * limit);
  const totalPages = Math.ceil(todayExcs.length / limit) || 1;

  return (
    <Box>
      <DashboardBanner />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<TrendingUp />}
          onClick={loadData}
          sx={{ borderRadius: 2 }}
        >
          Обновить
        </Button>
      </Box>

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <StyledCard onClick={() => card.onClick ? card.onClick() : handleCardClick(card.path)}>
              <CardContent sx={{
                background: `linear-gradient(135deg, ${card.color} 0%, ${card.color}dd 100%)`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 2,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                }
              }}>
                <Avatar sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  width: 48,
                  height: 48,
                  mb: 0.5,
                  '& .MuiSvgIcon-root': { fontSize: 28, color: 'white' }
                }}>
                  {card.icon}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.4rem' }}>
                  {card.value}
                </Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.8rem', opacity: 0.8 }}>
                  {card.title}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
          ⚡ Быстрые действия
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/guides')}
              sx={{ borderRadius: 2, py: 1.5, textTransform: 'none', fontWeight: 500, bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
            >
              Добавить экскурсовода
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<ReceiptLong />}
              onClick={() => navigate('/summary')}
              sx={{ borderRadius: 2, py: 1.5, textTransform: 'none', fontWeight: 500, bgcolor: '#ed6c02', '&:hover': { bgcolor: '#e65100' } }}
            >
              Сформировать акт
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Tour />}
              onClick={() => navigate('/tours')}
              sx={{ borderRadius: 2, py: 1.5, textTransform: 'none', fontWeight: 500, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Добавить программу
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Extension />}
              onClick={() => navigate('/extra-programs')}
              sx={{ borderRadius: 2, py: 1.5, textTransform: 'none', fontWeight: 500, bgcolor: '#9c27b0', '&:hover': { bgcolor: '#6a1b9a' } }}
            >
              Добавить доп. программу
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* БЛОК: Экскурсии на дату с ПАГИНАЦИЕЙ */}
      <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth color="primary" /> Экскурсии на дату
          </Typography>
          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setPage(1);
            }}
            sx={{ width: 180 }}
          />
        </Box>

        {loadingExcs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><LinearProgress sx={{ width: '100%' }} /></Box>
        ) : todayExcs.length === 0 && !loadingExcs ? (
          <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
            На этот день нет назначенных экскурсий
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1, px: 1.5, py: 1, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Chip label={`Всего: ${todayExcs.length}`} size="small" />
                <Chip
                  label={`В календаре: ${todayExcs.filter((e: any) => e.state === 'in_calendar' || e.calendar_id).length}`}
                  size="small" color="success" variant="outlined"
                />
                <Chip
                  label={`Не утверждено: ${todayExcs.filter((e: any) => e.state === 'not_approved' || (e.assignment_id && !e.calendar_id)).length}`}
                  size="small" color="warning" variant="outlined"
                />
                <Chip
                  label={`Не распределено: ${todayExcs.filter((e: any) => e.state === 'unassigned' || !e.assignment_id).length}`}
                  size="small" color="error" variant="outlined"
                />
              </Box>
            </Grid>
            
            {/* ИСПОЛЬЗУЕМ displayedExcs ВМЕСТО todayExcs */}
            {displayedExcs.map((exc, idx) => (
              <Grid key={idx} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card variant="outlined" sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderLeft: '4px solid', 
                  borderColor: exc.state === 'in_calendar' || exc.calendar_id ? 'success.main' : exc.state === 'not_approved' || exc.assignment_id ? 'warning.main' : 'error.main' 
                }}>
                  <CardContent sx={{ p: '12px !important', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold', color: 'text.secondary' }}>
                        <AccessTime fontSize="small" /> {exc.time?.slice(0, 5) || '--:--'}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label={exc.is_english ? 'EN' : 'RU'} size="small" color={exc.is_english ? 'info' : 'default'} sx={{ height: 20, fontSize: '0.7rem' }} />
                        <Chip label={(exc.people_count ?? 0) + ' чел.'} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        <Chip
                          label={exc.calendar_id ? '✓ В календаре' : exc.assignment_id ? 'Не утверждено' : 'Не распределено'}
                          size="small"
                          color={exc.calendar_id ? 'success' : exc.assignment_id ? 'warning' : 'error'}
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{exc.tour_name}</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Person fontSize="small" color={exc.guide_name ? 'primary' : 'error'} />
                      <Typography variant="body2" color={exc.assignment_id ? 'text.primary' : 'error.main'}>
                        {exc.guide_short || (exc.assignment_id ? 'Нет гида' : 'Не распределено')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto', pt: 1 }} noWrap>
                      Заказчик: {exc.customer || '—'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            
            {/* КОМПОНЕНТ ПАГИНАЦИИ */}
            {todayExcs.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(_, p) => setPage(p)} 
                color="primary" 
                showFirstButton 
                showLastButton
                sx={{ display: 'flex', justifyContent: 'flex-start' }}
              />
              <TextField
                select
                size="small"
                label="На странице"
                value={itemsPerPage}
                onChange={(e) => {
                  const val = e.target.value;
                  setItemsPerPage(val === 'all' ? 'all' : Number(val));
                  setPage(1);
                }}
                sx={{ width: 130 }}
              >
                <MenuItem value={6}>6</MenuItem>
                <MenuItem value={9}>9</MenuItem>
                <MenuItem value={12}>12</MenuItem>
                <MenuItem value="all">Все</MenuItem>
              </TextField>
            </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

      {showGuidesWithoutCalendar && guidesWithoutCalendar.length > 0 && (
        <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <Warning sx={{ mr: 1, color: '#e65100', verticalAlign: 'middle' }} />
              Экскурсоводы без календаря ({guidesWithoutCalendar.length})
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowGuidesWithoutCalendar(false)}
            >
              Скрыть
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {guidesWithoutCalendar.map((guide) => (
              <Grid key={guide.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card sx={{ borderRadius: 2, bgcolor: '#fff8e1' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {guide.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Договор: {guide.contract_number || 'Нет'}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleOpenExtraDialog(guide)}
                      sx={{ mt: 1 }}
                    >
                      Назначить доп. программы
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%', minHeight: 350 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <EmojiEvents color="warning" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Топ экскурсоводов
              </Typography>
              <Chip label="За период" size="small" variant="outlined" sx={{ ml: 1 }} />
            </Box>
            {topGuides.length === 0 ? (
              <Typography color="textSecondary">Нет данных за период</Typography>
            ) : (
              topGuides.map((guide, index) => (
                <Box key={index} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : 'action.selected',
                          width: 28,
                          height: 28,
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        {index + 1}
                      </Avatar>
                      <Typography>{guide.name}</Typography>
                    </Box>
                    <Chip label={`${guide.tours_count} экск.`} size="small" />
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%', minHeight: 350 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Groups color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Топ популяризаторов
              </Typography>
              <Chip label="По людям" size="small" variant="outlined" sx={{ ml: 1 }} />
            </Box>
            {topPopularizers.length === 0 ? (
              <Typography color="textSecondary">Нет данных</Typography>
            ) : (
              topPopularizers.map((guide, index) => (
                <Box key={index} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : 'action.selected',
                          width: 28,
                          height: 28,
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        {index + 1}
                      </Avatar>
                      <Typography>{guide.name}</Typography>
                    </Box>
                    <Chip label={`${guide.total_people} чел.`} size="small" color="primary" variant="outlined" />
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%', minHeight: 350 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Star color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Топ программ
              </Typography>
              <Chip label="За период" size="small" variant="outlined" sx={{ ml: 1 }} />
            </Box>
            {topTours.length === 0 ? (
              <Typography color="textSecondary">Нет данных за период</Typography>
            ) : (
              topTours.map((tour, index) => (
                <Box key={index} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 500 }}>{tour.name}</Typography>
                    <Chip label={`${tour.count} раз`} size="small" />
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={openExtraDialog} onClose={handleCloseExtraDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Extension color="secondary" />
            <Typography variant="h6">
              Назначить доп. программы
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {selectedGuide?.name} — Договор: {selectedGuide?.contract_number || 'Нет'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loadingExisting ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <LinearProgress sx={{ width: '100%' }} />
              </Box>
            ) : (
              <>
                {selectedPrograms.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Добавленные программы ({selectedPrograms.length}):
                    </Typography>
                    <List dense sx={{ bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      {selectedPrograms.map((prog) => (
                        <ListItem key={prog.extraProgramId} divider>
                          <ListItemText
                            primary={prog.name}
                            secondary={`${prog.hours} ч. × ${prog.rate.toLocaleString('ru-RU')} ₽ = ${(prog.hours * prog.rate).toLocaleString('ru-RU')} ₽${prog.id ? ' (существующая)' : ''}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="error"
                              size="small"
                              onClick={() => handleRemoveProgram(prog.extraProgramId)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </>
            )}

            <Divider />

            <Typography variant="subtitle2" gutterBottom>
              Добавить программу:
            </Typography>

            {duplicateWarning.show && (
              <Alert severity="warning" icon={<Info />} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>"{duplicateWarning.programName}"</strong> уже добавлена ({duplicateWarning.existingHours} ч.).
                  {hours > 0 && ` Будет добавлено еще ${hours} ч. (всего: ${duplicateWarning.existingHours + hours} ч.)`}
                </Typography>
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>Выберите дополнительную программу</InputLabel>
              <Select
                value={selectedExtraProgram}
                onChange={(e) => handleProgramSelect(Number(e.target.value))}
                label="Выберите дополнительную программу"
                displayEmpty
              >
                <MenuItem value={0} disabled>
                  <em>Выберите дополнительную программу</em>
                </MenuItem>
                {extraPrograms.map((p) => {
                  const isAdded = selectedPrograms.some(sp => sp.extraProgramId === p.id);
                  return (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} ({Number(p.rate).toLocaleString('ru-RU')} руб.)
                      {isAdded && ' ✅'}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Часы"
                type="number"
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                fullWidth
                inputProps={{ min: 0.5, step: 0.5 }}
              />
              <TextField
                label="Ставка"
                type="number"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>₽</Typography>,
                }}
                inputProps={{ min: 0, step: 50 }}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddProgram}
              disabled={!selectedExtraProgram || hours <= 0 || rate <= 0}
              fullWidth
            >
              {duplicateWarning.show ? 'Обновить (увеличить часы)' : 'Добавить в список'}
            </Button>

            {selectedPrograms.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Итого: <strong>{selectedPrograms.reduce((sum, p) => sum + p.hours * p.rate, 0).toLocaleString('ru-RU')} ₽</strong>
                  ({selectedPrograms.length} программ)
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtraDialog}>Отмена</Button>
          <Button
            variant="contained"
            startIcon={<Check />}
            onClick={handleApplyPrograms}
            disabled={selectedPrograms.length === 0 || saving}
            color="primary"
          >
            {saving ? 'Сохранение...' : 'Применить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
